<template>
  <v-app>
    <v-container fluid>
      <v-card variant="outlined">
        <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4 text-subtitle-1">
          <span>Production Material Issue List</span>
          <v-spacer />
          <v-text-field v-model="search" label="Search" prepend-inner-icon="mdi-magnify" density="compact" variant="outlined" hide-details clearable style="max-width: 280px" />
          <v-btn color="primary" prepend-icon="mdi-plus" :to="{ name: 'production-requisition-delivery' }">Issue Raw Materials</v-btn>
        </v-card-title>
        <v-data-table :headers="headers" :items="items" :search="search" :loading="loading" density="compact">
          <template #item.index="{ index }">{{ index + 1 }}</template>
          <template #item.requisition="{ item }">{{ item.requisition_master?.code || '-' }}</template>
          <template #item.warehouse="{ item }">{{ item.warehouse?.name || '-' }}</template>
          <template #item.materials="{ item }">
            <div v-for="detail in item.requisition_delivery_details || []" :key="detail.id" class="text-caption mb-1">
              <strong>{{ detail.product?.name }}</strong> · {{ number(detail.qty) }}
              <div v-for="lot in detail.allocations || []" :key="lot.id" class="text-medium-emphasis">
                {{ lot.lot_no || lot.batch_no || '-' }} · purity {{ number(lot.purchase_purity, 4) }}% · {{ number(lot.quantity) }}
              </div>
            </div>
          </template>
          <template #item.amount="{ item }">{{ number(item.amount, 2) }}</template>
          <template #item.approval_status="{ item }">
            <v-chip size="small" :color="approvalColor(item.approval_status)">{{ item.approval_status || (item.status ? 'Pending' : 'Draft') }}</v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-btn icon="mdi-file-document-outline" color="blue" variant="text" size="small" :to="{ name: 'production-requisition-delivery-details', params: { id: item.id } }" />
            <v-btn v-if="canEdit(item) && $updateAccess($route.path)" icon="mdi-pencil-outline" color="primary" variant="text" size="small" :to="{ name: 'production-requisition-delivery', query: { id: item.id } }" />
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const store = useStore();
const search = ref("");
const loading = ref(false);
const items = computed(() => store.getters.getInvProductionRequisitionDeliveryList || []);
const headers = [
  { title: "#", key: "index", sortable: false },
  { title: "Issue Code", key: "code" },
  { title: "Requisition", key: "requisition" },
  { title: "Issue Date", key: "requisition_delivery_date" },
  { title: "Warehouse", key: "warehouse" },
  { title: "Material / Lot / Purity / Qty", key: "materials", sortable: false },
  { title: "Cost", key: "amount", align: "end" },
  { title: "Approval", key: "approval_status" },
  { title: "Actions", key: "actions", sortable: false },
];

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function approvalColor(status) {
  if (status === "Approved") return "success";
  if (status === "Cancelled" || status === "Rejected") return "error";
  return "warning";
}

function canEdit(item) {
  return !item.posted_at && !item.approval_current_process && (!item.approval_status || item.approval_status === "Pending") && Number(item.status) === 0;
}

onMounted(async () => {
  loading.value = true;
  try {
    await store.dispatch("getInvProductionRequisitionDeliveryList", {});
  } catch (error) {
    toast.error(error?.response?.data?.message || "Production material issue list could not be loaded.");
  } finally {
    loading.value = false;
  }
});
</script>
